import { Theme } from '@monorepo/relayably/theme'
import React, { useEffect, useMemo, useState } from 'react'
import { LayoutChangeEvent, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, useWindowDimensions, View } from 'react-native'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import Svg, { Path } from 'react-native-svg'
import { DashboardAgentDetail } from '../types'

const hexToRgba = (hex: string, alpha: number) => {
  const value = hex.replace('#', '')
  const expanded =
    value.length === 3
      ? value
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : value
  const r = parseInt(expanded.slice(0, 2), 16)
  const g = parseInt(expanded.slice(2, 4), 16)
  const b = parseInt(expanded.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const shortenText = (value: string, maxLength: number) => {
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (normalized.length <= maxLength) {
    return normalized
  }

  return `${normalized.slice(0, maxLength - 1).trimEnd()}...`
}

const formatEdgeLabel = (targetLabel: string, edgeLabel: string) => {
  const normalizedTarget = targetLabel.toLowerCase()
  if (normalizedTarget.includes('support')) {
    return 'Support'
  }
  if (normalizedTarget.includes('sales')) {
    return 'Sales'
  }
  if (normalizedTarget.includes('screen')) {
    return 'Screen'
  }

  return shortenText(edgeLabel, 28)
}

const sanitizeWorkflowLabel = (value: string) => {
  const normalized = value.replace(/\s+/g, ' ').trim()
  const parts = normalized.split('-')

  if (parts.length > 1 && /^[A-Za-z0-9]{12,}$/.test(parts[0])) {
    return parts.slice(1).join('-')
  }

  return normalized
}

type RenderWorkflowNodeType = 'start' | 'end' | 'override_agent' | 'branch'

type RenderWorkflowNode = {
  id: string
  label: string
  prompt: string
  type: RenderWorkflowNodeType
  x: number
  y: number
  width: number
  height: number
}

type RenderWorkflowEdge = {
  id: string
  source: string
  target: string
  path: string
}

const estimateTextLines = (value: string, charsPerLine: number, maxLines: number) => {
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (!normalized) {
    return 1
  }

  return Math.max(1, Math.min(maxLines, Math.ceil(normalized.length / charsPerLine)))
}

const getNodeDimensions = (type: RenderWorkflowNodeType, label: string, prompt: string) => {
  if (type === 'start' || type === 'end') {
    return { width: 120, height: 72 }
  }

  if (type === 'branch') {
    const width = Math.max(96, Math.min(132, label.length * 9 + 28))
    return { width, height: 48 }
  }

  const promptLines = estimateTextLines(prompt, 36, 3)
  const labelLines = estimateTextLines(label, 18, 2)
  const width = 300
  const height = 72 + labelLines * 12 + promptLines * 18

  return { width, height }
}

const buildEdgePath = (source: RenderWorkflowNode, target: RenderWorkflowNode) => {
  const sourceCenterX = source.x + source.width / 2
  const targetCenterX = target.x + target.width / 2
  const startX = sourceCenterX
  const startY = source.y + source.height
  const endX = targetCenterX
  const endY = target.y
  const gapAboveTarget = 18
  const minVerticalRun = 40
  const preferredBendY = startY + Math.max(minVerticalRun, (endY - startY) / 2)
  const maxBendYBeforeTarget = endY - gapAboveTarget
  const bendY =
    maxBendYBeforeTarget > startY + 8
      ? Math.min(preferredBendY, maxBendYBeforeTarget)
      : startY + Math.max(20, (endY - startY) / 2)

  return `M ${startX} ${startY} L ${startX} ${bendY} L ${endX} ${bendY} L ${endX} ${endY}`
}

export const AgentDetailScreen = ({
  theme,
  agent,
  onSave,
  onDeploy,
  activeTab: controlledActiveTab,
  onTabChange,
}: {
  theme: Theme
  agent: DashboardAgentDetail
  onSave: (payload: { name: string; description: string; greeting: string; prompt: string }) => void
  onDeploy: (payload: { name: string; description: string; greeting: string; prompt: string }) => void
  activeTab?: 'details' | 'workflow'
  onTabChange?: (tab: 'details' | 'workflow') => void
}) => {
  const WorkflowViewport = View as any
  const { width } = useWindowDimensions()
  const isCompact = width < 760
  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState<'details' | 'workflow'>('details')
  const [selectedNodeId, setSelectedNodeId] = useState<string>('entry')
  const [name, setName] = useState(agent.agent.name)
  const [description, setDescription] = useState(agent.agent.description)
  const [greeting, setGreeting] = useState(agent.agent.greeting)
  const [prompt, setPrompt] = useState(agent.agent.prompt)
  const [zoomPercent, setZoomPercent] = useState(100)
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 })
  const styles = createStyles(theme)
  const scale = useSharedValue(1)
  const panX = useSharedValue(0)
  const panY = useSharedValue(0)
  const startPanX = useSharedValue(0)
  const startPanY = useSharedValue(0)
  const startScale = useSharedValue(1)
  const activeTab = controlledActiveTab ?? uncontrolledActiveTab
  const setActiveTab = (tab: 'details' | 'workflow') => {
    if (controlledActiveTab === undefined) {
      setUncontrolledActiveTab(tab)
    }
    onTabChange?.(tab)
  }

  useEffect(() => {
    if (controlledActiveTab === undefined) {
      setUncontrolledActiveTab('details')
    }
    setSelectedNodeId(agent.workflow.nodes[0]?.id ?? '')
    setName(agent.agent.name)
    setDescription(agent.agent.description)
    setGreeting(agent.agent.greeting)
    setPrompt(agent.agent.prompt)
  }, [agent, controlledActiveTab])

  const workflowLayout = useMemo(() => {
    const topPadding = 64
    const leftPadding = 72
    const sourceNodes = [...agent.workflow.nodes].sort((left, right) => (left.x === right.x ? left.y - right.y : left.x - right.x))
    const minX = Math.min(...sourceNodes.map(node => node.x), 0)
    const minY = Math.min(...sourceNodes.map(node => node.y), 0)

    const renderNodes: RenderWorkflowNode[] = []
    const renderEdges: RenderWorkflowEdge[] = []
    const renderNodeMap = new Map<string, RenderWorkflowNode>()

    sourceNodes.forEach(node => {
      const displayLabel = sanitizeWorkflowLabel(node.label)
      const prompt =
        node.type === 'start'
          ? 'Conversation starts here.'
          : node.type === 'end'
          ? 'Conversation ends here.'
          : node.prompt || 'Step in the workflow.'
      const dimensions = getNodeDimensions(node.type, displayLabel, prompt)
      const renderNode: RenderWorkflowNode = {
        id: node.id,
        label: displayLabel,
        prompt,
        type: node.type,
        x: leftPadding + (node.x - minX),
        y: topPadding + (node.y - minY),
        width: dimensions.width,
        height: dimensions.height,
      }
      renderNodes.push(renderNode)
      renderNodeMap.set(renderNode.id, renderNode)
    })

    let branchCounter = 0
    const conditionalEdges = agent.workflow.edges.filter(edge => edge.conditionType === 'llm')
    const unconditionalEdges = agent.workflow.edges.filter(edge => edge.conditionType !== 'llm')

    unconditionalEdges.forEach(edge => {
      const source = renderNodeMap.get(edge.source)
      const target = renderNodeMap.get(edge.target)
      if (!source || !target) {
        return
      }

      renderEdges.push({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        path: buildEdgePath(source, target),
      })
    })

    const conditionalGroups = new Map<string, typeof conditionalEdges>()
    conditionalEdges.forEach(edge => {
      const group = conditionalGroups.get(edge.source) ?? []
      group.push(edge)
      conditionalGroups.set(edge.source, group)
    })

    conditionalGroups.forEach(group => {
      const source = renderNodeMap.get(group[0]?.source ?? '')
      if (!source) {
        return
      }

      const sortedGroup = [...group].sort((left, right) => {
        const leftTarget = renderNodeMap.get(left.target)
        const rightTarget = renderNodeMap.get(right.target)
        return (leftTarget?.y ?? 0) - (rightTarget?.y ?? 0)
      })
      const branchGap = 14
      const branchNodes = sortedGroup.map(edge => {
        const target = renderNodeMap.get(edge.target)
        const branchLabel = formatEdgeLabel(target?.label ?? edge.target, edge.label)
        const branchDimensions = getNodeDimensions('branch', branchLabel, edge.label)
        return {
          edge,
          target,
          label: branchLabel,
          prompt: edge.label,
          width: branchDimensions.width,
          height: branchDimensions.height,
        }
      })
      const totalWidth = branchNodes.reduce((sum, branch) => sum + branch.width, 0) + Math.max(0, branchNodes.length - 1) * branchGap
      let currentX = source.x + source.width / 2 - totalWidth / 2
      const tallestBranch = Math.max(...branchNodes.map(branch => branch.height), 48)
      const minTargetTop = Math.min(...branchNodes.map(branch => branch.target?.y ?? Number.MAX_SAFE_INTEGER))
      const branchY = Math.max(source.y + source.height + 10, Math.min(source.y + source.height + 18, minTargetTop - tallestBranch - 14))

      branchNodes.forEach(branch => {
        if (!branch.target) {
          return
        }

        const branchNode: RenderWorkflowNode = {
          id: `branch-${(branchCounter += 1)}`,
          label: branch.label,
          prompt: branch.prompt,
          type: 'branch',
          x: currentX,
          y: branchY,
          width: branch.width,
          height: branch.height,
        }

        currentX += branch.width + branchGap
        renderNodes.push(branchNode)
        renderNodeMap.set(branchNode.id, branchNode)

        renderEdges.push({
          id: `${branch.edge.id}-source`,
          source: source.id,
          target: branchNode.id,
          path: buildEdgePath(source, branchNode),
        })
        renderEdges.push({
          id: `${branch.edge.id}-target`,
          source: branchNode.id,
          target: branch.target.id,
          path: buildEdgePath(branchNode, branch.target),
        })
      })
    })

    const maxX = renderNodes.reduce((accumulator, node) => Math.max(accumulator, node.x + node.width), 0)
    const maxY = renderNodes.reduce((accumulator, node) => Math.max(accumulator, node.y + node.height), 0)

    return {
      nodes: renderNodes,
      edges: renderEdges,
      bounds: {
        width: Math.max(1480, maxX + 120),
        height: Math.max(620, maxY + 80),
      },
    }
  }, [agent.workflow.edges, agent.workflow.nodes])

  const selectedNode = workflowLayout.nodes.find(node => node.id === selectedNodeId) ?? workflowLayout.nodes[0] ?? null

  const canvasAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: panX.value }, { translateY: panY.value }, { scale: scale.value }],
  }))

  const setZoomLevel = (nextScale: number) => {
    const boundedScale = Math.min(2.4, Math.max(0.55, nextScale))
    scale.value = withTiming(boundedScale)
    setZoomPercent(Math.round(boundedScale * 100))
  }

  useEffect(() => {
    if (!viewportSize.width || !viewportSize.height || !workflowLayout.bounds.width || !workflowLayout.bounds.height) {
      return
    }

    const horizontalScale = viewportSize.width / workflowLayout.bounds.width
    const verticalScale = viewportSize.height / workflowLayout.bounds.height
    const fittedScale = Math.min(1, Math.max(0.55, Math.min(horizontalScale, verticalScale) * 0.92))
    scale.value = fittedScale
    panX.value = (viewportSize.width - workflowLayout.bounds.width * fittedScale) / 2
    panY.value = Math.max(16, (viewportSize.height - workflowLayout.bounds.height * fittedScale) / 2)
    setZoomPercent(Math.round(fittedScale * 100))
  }, [panX, panY, scale, viewportSize.height, viewportSize.width, workflowLayout.bounds.height, workflowLayout.bounds.width])

  const handleViewportLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout
    setViewportSize(current => (current.width === width && current.height === height ? current : { width, height }))
  }

  const handleWheel = (event: any) => {
    if (Platform.OS !== 'web') {
      return
    }

    const nativeEvent = event?.nativeEvent
    if (!nativeEvent) {
      return
    }

    event.preventDefault?.()
    const delta = nativeEvent.deltaY < 0 ? 0.1 : -0.1
    const nextScale = Math.min(2.4, Math.max(0.55, scale.value + delta))
    scale.value = nextScale
    setZoomPercent(Math.round(nextScale * 100))
  }

  const panGesture = Gesture.Pan()
    .onStart(() => {
      startPanX.value = panX.value
      startPanY.value = panY.value
    })
    .onUpdate(event => {
      panX.value = startPanX.value + event.translationX
      panY.value = startPanY.value + event.translationY
    })

  const pinchGesture = Gesture.Pinch()
    .onStart(() => {
      startScale.value = scale.value
    })
    .onUpdate(event => {
      const nextScale = startScale.value * event.scale
      const boundedScale = Math.min(2.4, Math.max(0.55, nextScale))
      scale.value = boundedScale
      runOnJS(setZoomPercent)(Math.round(boundedScale * 100))
    })

  const doubleTapGesture = Gesture.Tap()
    .numberOfTaps(2)
    .onEnd(() => {
      scale.value = withTiming(1)
      panX.value = withTiming(0)
      panY.value = withTiming(0)
      runOnJS(setZoomPercent)(100)
    })

  const composedGesture = Gesture.Simultaneous(panGesture, pinchGesture, doubleTapGesture)

  return (
    <View style={styles.screen}>
      {isCompact ? (
        <View style={styles.topArea}>
          <View style={styles.tabBar}>
            <Pressable
              onPress={() => setActiveTab('details')}
              style={[styles.tabButton, activeTab === 'details' ? styles.tabButtonActive : undefined]}
            >
              <Text style={[styles.tabButtonText, activeTab === 'details' ? styles.tabButtonTextActive : undefined]}>Details</Text>
            </Pressable>
            <Pressable
              onPress={() => setActiveTab('workflow')}
              style={[styles.tabButton, activeTab === 'workflow' ? styles.tabButtonActive : undefined]}
            >
              <Text style={[styles.tabButtonText, activeTab === 'workflow' ? styles.tabButtonTextActive : undefined]}>Workflow</Text>
            </Pressable>
          </View>
        </View>
      ) : null}
      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        {activeTab === 'details' ? (
          <View style={styles.editorPanel}>
            <Text style={styles.sectionTitle}>{agent.agent.name}</Text>
            <Text style={styles.sectionBody}>
              Update the plain-language description, opening line, and the core prompt this agent follows.
            </Text>

            <Text style={styles.label}>Agent name</Text>
            <TextInput value={name} onChangeText={setName} style={styles.input} placeholderTextColor={theme.colors.inactive} />

            <Text style={styles.label}>Short description</Text>
            <TextInput
              value={description}
              onChangeText={setDescription}
              style={[styles.input, styles.multiline]}
              multiline
              placeholderTextColor={theme.colors.inactive}
            />

            <Text style={styles.label}>Greeting</Text>
            <TextInput value={greeting} onChangeText={setGreeting} style={[styles.input, styles.multiline]} multiline />

            <Text style={styles.label}>Prompt</Text>
            <TextInput value={prompt} onChangeText={setPrompt} style={[styles.input, styles.promptInput]} multiline />

            <View style={styles.actionsRow}>
              <Pressable onPress={() => onSave({ name, description, greeting, prompt })} style={styles.secondaryButton}>
                <Text style={styles.secondaryButtonText}>Save draft</Text>
              </Pressable>
              <Pressable onPress={() => onDeploy({ name, description, greeting, prompt })} style={styles.saveButton}>
                <Text style={styles.saveButtonText}>Deploy live</Text>
              </Pressable>
            </View>
          </View>
        ) : (
          <View style={styles.workflowPanel}>
            <Text style={styles.sectionTitle}>Workflow</Text>
            <Text style={styles.sectionBody}>Scroll around the map to see how this agent listens, decides, and routes the call.</Text>

            {agent.agent.assignedPhoneNumber ? (
              <View style={styles.assignmentCard}>
                <Text style={styles.assignmentLabel}>Assigned number</Text>
                <Text style={styles.assignmentValue}>{agent.agent.assignedPhoneNumber}</Text>
                {agent.agent.assignedPhoneNumberLabel ? (
                  <Text style={styles.assignmentHint}>{agent.agent.assignedPhoneNumberLabel}</Text>
                ) : null}
              </View>
            ) : null}

            <View style={styles.workflowHintRow}>
              <Text style={styles.workflowHint}>Drag to move. Pinch to zoom. Double tap to reset.</Text>
              <View style={styles.workflowControls}>
                <Pressable onPress={() => setZoomLevel(scale.value - 0.2)} style={styles.zoomButton}>
                  <Text style={styles.zoomButtonText}>-</Text>
                </Pressable>
                <Text style={styles.zoomValue}>{zoomPercent}%</Text>
                <Pressable onPress={() => setZoomLevel(scale.value + 0.2)} style={styles.zoomButton}>
                  <Text style={styles.zoomButtonText}>+</Text>
                </Pressable>
                <Pressable
                  onPress={() => {
                    scale.value = withTiming(1)
                    panX.value = withTiming(0)
                    panY.value = withTiming(0)
                  }}
                  style={styles.resetViewButton}
                >
                  <Text style={styles.resetViewButtonText}>Reset view</Text>
                </Pressable>
              </View>
            </View>

            <WorkflowViewport
              onLayout={handleViewportLayout}
              onWheel={handleWheel as any}
              style={[styles.workflowViewport, Platform.OS === 'web' ? ({ cursor: 'grab', touchAction: 'none' } as any) : null]}
            >
              <GestureDetector gesture={composedGesture}>
                <Animated.View
                  style={[
                    styles.workflowCanvas,
                    { width: workflowLayout.bounds.width, height: workflowLayout.bounds.height },
                    canvasAnimatedStyle,
                  ]}
                >
                  <Svg
                    height={workflowLayout.bounds.height}
                    pointerEvents='none'
                    style={styles.workflowSvg}
                    width={workflowLayout.bounds.width}
                  >
                    {workflowLayout.edges.map(edge => (
                      <Path
                        key={edge.id}
                        d={edge.path}
                        fill='none'
                        stroke={hexToRgba(
                          theme.colors.primary,
                          edge.source === selectedNodeId || edge.target === selectedNodeId ? 0.72 : 0.28
                        )}
                        strokeWidth={edge.source === selectedNodeId || edge.target === selectedNodeId ? 3.5 : 2.5}
                      />
                    ))}
                  </Svg>

                  {workflowLayout.nodes.map(node => {
                    const isBranch = node.type === 'branch'
                    const isStartOrEnd = node.type === 'start' || node.type === 'end'
                    const isSelected = node.id === selectedNodeId
                    const displayLabel = node.label
                    const canvasPrompt = node.prompt

                    return (
                      <Pressable
                        key={node.id}
                        onPress={() => setSelectedNodeId(node.id)}
                        style={[
                          styles.workflowNodeAbsolute,
                          node.type === 'start'
                            ? styles.startNode
                            : node.type === 'end'
                            ? styles.endNode
                            : node.type === 'branch'
                            ? styles.branchNode
                            : styles.overrideNode,
                          isSelected ? styles.workflowNodeSelected : undefined,
                          {
                            left: node.x,
                            top: node.y,
                            width: node.width,
                            minHeight: node.height,
                            borderRadius: isStartOrEnd ? 999 : 20,
                            zIndex: 2,
                          },
                        ]}
                      >
                        <View style={isBranch ? styles.branchContent : undefined}>
                          <Text numberOfLines={2} style={[styles.workflowNodeLabel, isBranch ? styles.branchNodeLabel : undefined]}>
                            {displayLabel}
                          </Text>
                          {!isBranch ? (
                            <Text numberOfLines={3} style={styles.workflowNodePrompt}>
                              {canvasPrompt}
                            </Text>
                          ) : null}
                        </View>
                      </Pressable>
                    )
                  })}
                </Animated.View>
              </GestureDetector>
            </WorkflowViewport>

            {selectedNode ? (
              <View style={styles.selectionCard}>
                <Text style={styles.selectionLabel}>Selected step</Text>
                <Text style={styles.selectionTitle}>{selectedNode.label}</Text>
                <Text style={styles.selectionBody}>
                  {selectedNode.prompt || 'This step currently routes directly without extra explanation.'}
                </Text>
              </View>
            ) : null}

            <View style={styles.handoffStack}>
              {agent.agent.handoffs.map(handoff => (
                <View key={`${handoff.targetTemplateId}-${handoff.when}`} style={styles.handoffCard}>
                  <Text style={styles.handoffTitle}>{handoff.targetTemplateId}</Text>
                  <Text style={styles.handoffBody}>{handoff.when}</Text>
                </View>
              ))}
            </View>
          </View>
        )}
      </ScrollView>
    </View>
  )
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    screen: {
      flex: 1,
    },
    topArea: {
      paddingBottom: 12,
    },
    content: {
      gap: 18,
      paddingBottom: 24,
    },
    tabBar: {
      flexDirection: 'row',
      gap: 0,
      padding: 6,
      borderRadius: 18,
      backgroundColor: hexToRgba(theme.colors.surface, 0.94),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
      alignSelf: 'flex-start',
      overflow: 'hidden',
    },
    tabButton: {
      minWidth: 132,
      borderRadius: 14,
      paddingHorizontal: 20,
      paddingVertical: 12,
      backgroundColor: 'transparent',
      alignItems: 'center',
    },
    tabButtonActive: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.14),
    },
    tabButtonText: {
      color: theme.colors.inactive,
      fontSize: 14,
      fontWeight: '700',
    },
    tabButtonTextActive: {
      color: theme.colors.text,
    },
    editorPanel: {
      borderRadius: 24,
      padding: 20,
      backgroundColor: hexToRgba(theme.colors.surface, 0.96),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 12,
    },
    workflowPanel: {
      borderRadius: 24,
      padding: 20,
      backgroundColor: hexToRgba(theme.colors.surface, 0.96),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 16,
    },
    sectionTitle: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 28,
      lineHeight: 32,
    },
    sectionBody: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
      marginBottom: 6,
    },
    label: {
      color: theme.colors.inactive,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 0.8,
      textTransform: 'uppercase',
    },
    input: {
      borderRadius: 16,
      paddingHorizontal: 14,
      paddingVertical: 12,
      backgroundColor: hexToRgba(theme.colors.background, 0.72),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      color: theme.colors.text,
      fontSize: 14,
    },
    multiline: {
      minHeight: 88,
      textAlignVertical: 'top',
    },
    promptInput: {
      minHeight: 220,
      textAlignVertical: 'top',
    },
    saveButton: {
      borderRadius: 999,
      paddingHorizontal: 18,
      paddingVertical: 12,
      backgroundColor: theme.colors.primary,
    },
    saveButtonText: {
      color: theme.colors.staticWhite,
      fontSize: 14,
      fontWeight: '700',
    },
    actionsRow: {
      marginTop: 8,
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    secondaryButton: {
      borderRadius: 999,
      paddingHorizontal: 18,
      paddingVertical: 12,
      backgroundColor: hexToRgba(theme.colors.background, 0.72),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
    },
    secondaryButtonText: {
      color: theme.colors.text,
      fontSize: 14,
      fontWeight: '700',
    },
    assignmentCard: {
      borderRadius: 18,
      padding: 16,
      gap: 6,
      backgroundColor: hexToRgba(theme.colors.primary, 0.08),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.16),
    },
    assignmentLabel: {
      color: theme.colors.inactive,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 0.8,
      textTransform: 'uppercase',
    },
    assignmentValue: {
      color: theme.colors.text,
      fontSize: 20,
      fontWeight: '700',
    },
    assignmentHint: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
    },
    workflowHintRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    workflowControls: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 10,
    },
    workflowHint: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
    },
    zoomButton: {
      width: 38,
      height: 38,
      borderRadius: 999,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: hexToRgba(theme.colors.background, 0.72),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
    },
    zoomButtonText: {
      color: theme.colors.text,
      fontSize: 18,
      fontWeight: '700',
      lineHeight: 20,
    },
    zoomValue: {
      minWidth: 52,
      color: theme.colors.inactive,
      fontSize: 12,
      fontWeight: '700',
      textAlign: 'center',
    },
    resetViewButton: {
      borderRadius: 999,
      paddingHorizontal: 14,
      paddingVertical: 10,
      backgroundColor: hexToRgba(theme.colors.background, 0.72),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
    },
    resetViewButtonText: {
      color: theme.colors.text,
      fontSize: 12,
      fontWeight: '700',
    },
    workflowViewport: {
      height: 560,
      borderRadius: 24,
      overflow: 'hidden',
      backgroundColor: hexToRgba(theme.colors.background, 0.34),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.1),
    },
    workflowCanvas: {
      position: 'relative',
      backgroundColor: 'transparent',
    },
    workflowSvg: {
      position: 'absolute',
      left: 0,
      top: 0,
      zIndex: 0,
    },
    workflowNodeAbsolute: {
      position: 'absolute',
      borderRadius: 20,
      padding: 16,
      gap: 8,
      borderWidth: 1,
      overflow: 'hidden',
      zIndex: 1,
    },
    workflowNodeSelected: {
      shadowColor: theme.colors.primary,
      shadowOpacity: 0.28,
      shadowRadius: 18,
      shadowOffset: { width: 0, height: 10 },
      elevation: 10,
    },
    entryNode: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.12),
      borderColor: hexToRgba(theme.colors.primary, 0.18),
    },
    startNode: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.12),
      borderColor: hexToRgba(theme.colors.primary, 0.18),
      justifyContent: 'center',
      alignItems: 'center',
    },
    endNode: {
      backgroundColor: hexToRgba('#ff8f5a', 0.12),
      borderColor: hexToRgba('#ff8f5a', 0.22),
      justifyContent: 'center',
      alignItems: 'center',
    },
    branchNode: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.08),
      borderColor: hexToRgba(theme.colors.primary, 0.18),
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    branchContent: {
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 36,
    },
    overrideNode: {
      backgroundColor: hexToRgba('#61dcb6', 0.12),
      borderColor: hexToRgba('#61dcb6', 0.22),
    },
    workflowNodeLabel: {
      color: theme.colors.text,
      fontSize: 15,
      fontWeight: '700',
      textAlign: 'center',
    },
    branchNodeLabel: {
      fontSize: 13,
    },
    workflowNodePrompt: {
      color: theme.colors.inactive,
      fontSize: 12,
      lineHeight: 18,
      textAlign: 'center',
    },
    selectionCard: {
      borderRadius: 18,
      padding: 16,
      gap: 6,
      backgroundColor: hexToRgba(theme.colors.primary, 0.08),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.16),
    },
    selectionLabel: {
      color: theme.colors.inactive,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 0.8,
      textTransform: 'uppercase',
    },
    selectionTitle: {
      color: theme.colors.text,
      fontSize: 18,
      fontWeight: '700',
    },
    selectionBody: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
    },
    handoffStack: {
      gap: 12,
    },
    handoffCard: {
      borderRadius: 18,
      padding: 16,
      backgroundColor: hexToRgba(theme.colors.background, 0.56),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 6,
    },
    handoffTitle: {
      color: theme.colors.text,
      fontSize: 14,
      fontWeight: '700',
    },
    handoffBody: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
    },
  })
